import { z } from "zod";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { ServiceTemplateScheduleOccurrencesSortValue } from "@common/types/views/serviceTemplateScheduleOccurrencesView";

// ─── Occurrence response ───────────────────────────────────────────────────────

export const OccurrenceResponseSchema = z.object({
    id: z.string().uuid(),
    recurrenceId: z.string().uuid(),
    organizationId: z.string().uuid(),
    workspaceId: z.string().uuid().nullable(),
    occurrenceIndex: z.number().int().nonnegative(),
    applicationDate: z.string().date(),
    serviceStartDate: z.string().date(),
    serviceEndDate: z.string().date(),
    workStartDate: z.string().date(),
    workEndDate: z.string().date(),
    status: z.nativeEnum(ServiceTemplateRecurrenceOccurrenceStatus),
    preparerId: z.number().int().nullable(),
    reviewerId: z.number().int().nullable(),
    managerId: z.number().int().nullable(),
    clientId: z.number().int().nullable(),
    closeId: z.string().uuid().nullable(),
    appliedAt: z.coerce.date().nullable(),
    appliedEarly: z.boolean(),
    skippedAt: z.coerce.date().nullable(),
    createdByUserId: z.number().int().nullable(),
    updatedByUserId: z.number().int().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type OccurrenceResponse = z.infer<typeof OccurrenceResponseSchema>;

// ─── Resolved role occurrence (for capacity plan) ──────────────────────────────

export const ResolvedOccurrenceResponseSchema = OccurrenceResponseSchema.extend(
    {
        /** Role resolved as occurrence override ?? recurrence default. */
        resolvedPreparerId: z.number().int().nullable(),
        resolvedReviewerId: z.number().int().nullable(),
        resolvedManagerId: z.number().int().nullable(),
        resolvedClientId: z.number().int().nullable(),
    },
);

export type ResolvedOccurrenceResponse = z.infer<
    typeof ResolvedOccurrenceResponseSchema
>;

// ─── List query params ─────────────────────────────────────────────────────────

export const OccurrenceListQuerySchema = z.object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    status: z.nativeEnum(ServiceTemplateRecurrenceOccurrenceStatus).optional(),
    // Optional single-column sort. Omitted / cleared falls back to INDEX_ASC
    // (today's behavior) resolved in the occurrence service.
    sort: z.nativeEnum(ServiceTemplateScheduleOccurrencesSortValue).optional(),
});

export type OccurrenceListQuery = z.infer<typeof OccurrenceListQuerySchema>;

// ─── Role override payload ─────────────────────────────────────────────────────

export const OccurrenceRoleOverrideSchema = z.object({
    preparerId: z.number().int().nullable().optional(),
    reviewerId: z.number().int().nullable().optional(),
    managerId: z.number().int().nullable().optional(),
    clientId: z.number().int().nullable().optional(),
});

export type OccurrenceRoleOverride = z.infer<
    typeof OccurrenceRoleOverrideSchema
>;

// ─── Bulk action ───────────────────────────────────────────────────────

export const OccurrenceBulkActionTypeSchema = z.enum([
    "apply",
    "skip",
    "unskip",
    "delete",
]);

export type OccurrenceBulkActionType = z.infer<
    typeof OccurrenceBulkActionTypeSchema
>;

export const OccurrenceBulkActionBodySchema = z.object({
    occurrenceIds: z.array(z.string().uuid()).min(1).max(500),
    action: OccurrenceBulkActionTypeSchema,
});

export type OccurrenceBulkActionBody = z.infer<
    typeof OccurrenceBulkActionBodySchema
>;

export const OccurrenceBulkActionResponseSchema = z.object({
    succeededIds: z.array(z.string().uuid()),
    failed: z.array(
        z.object({
            occurrenceId: z.string().uuid(),
            message: z.string(),
        }),
    ),
});

export type OccurrenceBulkActionResponse = z.infer<
    typeof OccurrenceBulkActionResponseSchema
>;

// ─── Bulk role assignment ──────────────────────────────────────────────────────

export const OccurrenceBulkRoleAssignmentFieldSchema = z.enum([
    "preparerId",
    "reviewerId",
    "managerId",
    "clientId",
]);

export type OccurrenceBulkRoleAssignmentField = z.infer<
    typeof OccurrenceBulkRoleAssignmentFieldSchema
>;

export const OccurrenceBulkRoleAssignmentBodySchema = z.object({
    occurrenceIds: z.array(z.string().uuid()).min(1).max(500),
    field: OccurrenceBulkRoleAssignmentFieldSchema,
    value: z.number().int().nullable(),
});

export type OccurrenceBulkRoleAssignmentBody = z.infer<
    typeof OccurrenceBulkRoleAssignmentBodySchema
>;

// ─── Capacity plan query ───────────────────────────────────────────────────────

export const CapacityPlanQuerySchema = z.object({
    from: z.string().date(),
    to: z.string().date(),
    workspaceId: z.string().uuid().optional(),
});

export type CapacityPlanQuery = z.infer<typeof CapacityPlanQuerySchema>;

// ─── Capacity plan response ────────────────────────────────────────────────────

export const CapacityPlanResponseSchema = z.object({
    occurrences: z.array(ResolvedOccurrenceResponseSchema),
});

export type CapacityPlanResponse = z.infer<typeof CapacityPlanResponseSchema>;
